<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center px-4 h-14">
            <div class="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div class="px-4 pt-6 pb-20 space-y-3">
          <div v-for="i in 3" :key="i">
            <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-1.5"></div>
            <div class="h-11 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
          </div>
          <div class="pt-2">
            <div class="h-11 w-full bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded -->
    <template v-else>
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">
          {{ I18N.website_content.account_settings.security }}
        </h1>
      </div>
    </div>

    <div class="px-4 pt-6 pb-20">
      <form @submit.prevent="updatePassword" class="space-y-3">
        <div v-if="!user.need_to_set_password">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.website_content.account_settings.security_tab.old_password }}
          </label>
          <div class="relative">
            <input :type="showCurrent ? 'text' : 'password'"
                   v-model="user.current_password"
                   :placeholder="I18N.website_content.account_settings.security_tab.old_password"
                   class="w-full px-3.5 py-2.5 pr-10 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1C2128] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
            <button type="button" @click="showCurrent = !showCurrent" tabindex="-1"
                    class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg v-if="showCurrent" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
            </button>
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.website_content.account_settings.new_password }}
          </label>
          <div class="relative">
            <input :type="showNew ? 'text' : 'password'"
                   v-model="user.password"
                   :placeholder="I18N.website_content.account_settings.new_password"
                   class="w-full px-3.5 py-2.5 pr-10 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1C2128] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
            <button type="button" @click="showNew = !showNew" tabindex="-1"
                    class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg v-if="showNew" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
            </button>
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.website_content.account_settings.security_tab.confirm_password }}
          </label>
          <div class="relative">
            <input :type="showConfirm ? 'text' : 'password'"
                   v-model="user.password_confirmation"
                   :placeholder="I18N.website_content.account_settings.security_tab.confirm_password"
                   :class="errorPasswordMessage ? 'border-red-400 dark:border-red-500 focus:ring-red-400' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'"
                   class="w-full px-3.5 py-2.5 pr-10 text-sm border rounded-lg bg-white dark:bg-[#1C2128] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:border-transparent transition-all" />
            <button type="button" @click="showConfirm = !showConfirm" tabindex="-1"
                    class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg v-if="showConfirm" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
            </button>
          </div>
          <p v-if="errorPasswordMessage" class="mt-1.5 text-xs text-red-500">
            {{ I18N.website_content.account_settings.password_not_match }}
          </p>
        </div>

        <div class="pt-2">
          <button type="submit" :disabled="disableSubmitBtn"
                  class="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {{ user.need_to_set_password ? I18N.website_content.account_settings.security_tab.set_password : I18N.website_content.account_settings.change_password }}
          </button>
        </div>
      </form>
    </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
          <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
            <div class="flex divide-x divide-gray-100 dark:divide-gray-700">
              <div class="flex-1 px-6 py-5 space-y-4">
                <div v-for="i in 3" :key="i">
                  <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                  <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                </div>
                <div class="pt-2 flex justify-end">
                  <div class="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                </div>
              </div>
              <div class="w-72 flex-shrink-0 px-6 py-5 space-y-4">
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
                  <div class="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="h-2.5 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="space-y-3">
                  <div v-for="j in 3" :key="j" class="flex items-center gap-2.5">
                    <div class="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
                    <div class="h-2.5 w-40 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded -->
    <template v-else>
    <!-- Sub-page header -->
    <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
      <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
        <div class="flex items-center gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.security }}</h1>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
              {{ user.need_to_set_password ? I18N.website_content.account_settings.security_tab.set_password : I18N.website_content.account_settings.security_tab.title }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-5xl mx-auto px-6 py-6">
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

        <!-- Card header -->
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/>
            </svg>
          </div>
              <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">
              {{ user.need_to_set_password ? I18N.website_content.account_settings.security_tab.set_password : I18N.website_content.account_settings.change_password }}
            </h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
              {{ user.need_to_set_password ? 'Create a password for your account' : 'Update your account password' }}
            </p>
          </div>
        </div>

        <!-- Two-panel body -->
        <div class="flex divide-x divide-gray-100 dark:divide-gray-700">

          <!-- Left: form -->
          <form @submit.prevent="updatePassword" class="flex-1 px-6 py-5 space-y-4">
              <div v-if="!user.need_to_set_password">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  {{ I18N.website_content.account_settings.security_tab.old_password }}
                </label>
                <div class="relative">
                  <input :type="showCurrent ? 'text' : 'password'"
                         v-model="user.current_password"
                         :placeholder="I18N.website_content.account_settings.security_tab.old_password"
                         class="w-full px-3.5 py-2.5 pr-10 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                  <button type="button" @click="showCurrent = !showCurrent" tabindex="-1"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    <svg v-if="showCurrent" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  {{ I18N.website_content.account_settings.new_password }}
                </label>
                <div class="relative">
                  <input :type="showNew ? 'text' : 'password'"
                         v-model="user.password"
                         :placeholder="I18N.website_content.account_settings.new_password"
                         class="w-full px-3.5 py-2.5 pr-10 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                  <button type="button" @click="showNew = !showNew" tabindex="-1"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    <svg v-if="showNew" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  {{ I18N.website_content.account_settings.security_tab.confirm_password }}
                </label>
                <div class="relative">
                  <input :type="showConfirm ? 'text' : 'password'"
                         v-model="user.password_confirmation"
                         :placeholder="I18N.website_content.account_settings.security_tab.confirm_password"
                         :class="errorPasswordMessage ? 'border-red-400 dark:border-red-500 focus:ring-red-400' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'"
                         class="w-full px-3.5 py-2.5 pr-10 text-sm border rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:border-transparent transition-all" />
                  <button type="button" @click="showConfirm = !showConfirm" tabindex="-1"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    <svg v-if="showConfirm" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                  </button>
                </div>
                <p v-if="errorPasswordMessage" class="mt-1.5 flex items-center gap-1 text-xs text-red-500">
                  <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
                  {{ I18N.website_content.account_settings.password_not_match }}
                </p>
              </div>

            <div class="pt-2 flex justify-end">
              <button type="submit" :disabled="disableSubmitBtn"
                      class="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {{ user.need_to_set_password ? I18N.website_content.account_settings.security_tab.set_password : I18N.website_content.account_settings.change_password }}
              </button>
            </div>
          </form>

          <!-- Right: security tips -->
          <div class="w-72 flex-shrink-0 px-6 py-5 bg-white dark:bg-[#1C2128]">
            <div class="flex items-center gap-2.5 mb-3">
              <div class="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"/>
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Password tips</p>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Keep your account safe</p>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">A strong password protects your bookings and client data.</p>
            <ul class="space-y-3">
              <li class="flex items-start gap-2.5">
                <div class="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg class="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Use at least 8 characters</p>
              </li>
              <li class="flex items-start gap-2.5">
                <div class="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg class="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Mix letters, numbers and symbols</p>
              </li>
              <li class="flex items-start gap-2.5">
                <div class="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg class="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Never reuse passwords across sites</p>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import RequestMixin from 'controllers/vue_components/mixins/request'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import Constants from 'controllers/vue_components/mixins/constants'
import { useSettingsStore } from 'controllers/vue_components/settings/v1/store'

export default {
    name: 'Security',
    mixins: [RequestMixin, Constants],
    props: [],
    components: { FlashNotifications },

    data() {
        return {
            saving: false,
            showCurrent: false,
            showNew: false,
            showConfirm: false,
            user: {
                need_to_set_password: false,
                current_password: null,
                password: null,
                password_confirmation: null,
            },
        }
    },

    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },

        async updatePassword() {
            this.saving = true
            let data, response
            if (this.user.need_to_set_password) {
                data = { user: { password: this.user.password, password_confirmation: this.user.password_confirmation } }
                response = await this.axiosRequest('PUT', '/api/v1/users/change_password', data, true)
            } else {
                data = { user: { current_password: this.user.current_password, password: this.user.password, password_confirmation: this.user.password_confirmation } }
                response = await this.axiosRequest('PUT', '/users/passwords', data, true)
            }
            this.saving = false
            if (response.js_error === false && response.response.success) {
                this.user = { need_to_set_password: false, current_password: null, password: null, password_confirmation: null }
                this.showCurrent = false
                this.showNew = false
                this.showConfirm = false
            }
        },
    },

    computed: {
        disableSubmitBtn() {
            if (this.saving) return true
            if (this.user.need_to_set_password) {
                if (!this.user.password || !this.user.password_confirmation) return true
            } else {
                if (!this.user.current_password || !this.user.password || !this.user.password_confirmation) return true
            }
            return this.user.password !== this.user.password_confirmation
        },

        errorPasswordMessage() {
            return this.user.password !== this.user.password_confirmation && this.user.password_confirmation !== null
        },
    },

    created() {
        this.$watch(
            () => useSettingsStore().init_data_loaded,
            (loaded) => {
                if (loaded) this.user.need_to_set_password = useSettingsStore().current_user.need_to_set_password
            },
            { immediate: true }
        )
    },
}
</script>
